import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import { COMPANY } from "../../config/apiConfig";
import { alertDialog, confirmDialog } from "../../utils/dialogs";
import { resolveVoipSetup } from "../../utils/voip/voipSetup";
import { useApplication } from "../../contexts/ApplicationContext";

// O discador carrega os SDKs de voz sob demanda e este componente fica montado
// em todo o layout autenticado: a bandeja só é baixada na primeira abertura.
const DialerTray = lazy(() => import("./DialerTray"));

// Evento (window) que abre o discador (bandeja do botão flutuante do funil).
export const OPEN_DIALER_EVENT = "relinq:open-dialer";

const isEditableTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const tag = String(target.tagName || "").toUpperCase();
  return (
    target.isContentEditable ||
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT"
  );
};

/**
 * Discador global: o botão do funil (`OPEN_DIALER_EVENT`) ou a tecla D, em
 * qualquer tela autenticada e sem sair dela, abrem o `DialerTray`. **Só abre com
 * um VoIP integrado na empresa ativa**; sem ele, um aviso pequeno oferece levar
 * a pessoa à tela de integrações. Depois da primeira abertura a bandeja fica
 * montada para poder animar a saída. Montado em `routes/Container.jsx`.
 */
export default function GlobalDialer() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [company, setCompany] = useState(null);
  const openRef = useRef(false);
  const checkingRef = useRef(false);
  // O discador segue a chave da tela VoIP (`crm.card.view`): quem não a tem não
  // abre pelo botão do funil nem pela tecla D.
  const { can } = useApplication();
  const canDialRef = useRef(true);
  canDialRef.current = can("crm.card.view");

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const requestOpen = useCallback(async () => {
    if (openRef.current || checkingRef.current || !canDialRef.current) return;
    checkingRef.current = true;
    try {
      let activeCompany = null;
      try {
        const res = await qc.fetchQuery(
          ["taskmodal", "company-active"],
          () => axios.get(COMPANY.GET_ACTIVE),
          { staleTime: 20_000 },
        );
        activeCompany = res?.data?.data || res?.data || null;
      } catch (err) {
        console.error("Erro ao carregar a empresa ativa:", err);
        await alertDialog({
          title: "Não foi possível abrir o discador",
          message:
            "Não deu para verificar a integração de VoIP desta empresa. Tente novamente em instantes.",
          tone: "error",
        });
        return;
      }

      if (!resolveVoipSetup(activeCompany).configured) {
        const goIntegrations = await confirmDialog({
          title: "VoIP não integrado",
          message:
            "O discador só funciona com um VoIP integrado. Esta empresa ainda não tem um. Deseja ir para as integrações para cadastrar?",
          tone: "info",
          confirmLabel: "Ir para integrações",
          cancelLabel: "Agora não",
        });
        if (goIntegrations) navigate("/integracoes");
        return;
      }

      setCompany(activeCompany);
      setEverOpened(true);
      setOpen(true);
    } finally {
      checkingRef.current = false;
    }
  }, [navigate, qc]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key?.toLowerCase() !== "d") return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return;
      }
      if (event.repeat || event.isComposing || event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;
      if (!canDialRef.current) return;
      event.preventDefault();
      requestOpen();
    };
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_DIALER_EVENT, requestOpen);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_DIALER_EVENT, requestOpen);
    };
  }, [requestOpen]);

  if (!everOpened) return null;
  return (
    <Suspense fallback={null}>
      <DialerTray
        open={open}
        company={company}
        onClose={() => setOpen(false)}
      />
    </Suspense>
  );
}
